import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';

import { BeerPack } from 'utils/types/BeerPack';
import { HtmlContent, stripHtml } from 'utils/htmlContent';
import {
  calcPlanSavings,
  getHighlightedPackId,
  getPackQuantity,
  getPerBeerHint,
  getPlanFeatures,
  type PlanFeatureValue,
} from 'utils/subscriptionPlanFeatures';

const COLORS = {
  accent: '#c96c1e',
  accentHover: '#a85818',
  accentLight: 'rgba(201, 108, 30, 0.1)',
  accentLighter: 'rgba(201, 108, 30, 0.05)',
  foreground: '#1a1008',
  muted: '#6b5c4a',
  border: '#e8ddd1',
  surface: '#ffffff',
  savingsBg: '#e8f5e9',
  savingsInk: '#388e3c',
  mutedDisabled: 'rgba(107, 92, 74, 0.4)',
} as const;

interface SubscriptionPlansSectionProps {
  packs: BeerPack[];
  onSelectPlan: (pack: BeerPack) => void;
}

const planTitleSx = {
  fontSize: { xs: '1.125rem', md: '1.05rem' },
  fontWeight: 700,
  color: COLORS.accent,
  letterSpacing: '0.04em',
  lineHeight: 1.2,
  '& b, & strong': { fontWeight: 700 },
} as const;

const PlanTitle = ({ pack }: { pack: BeerPack }) => {
  const hint = getPerBeerHint(getPackQuantity(pack.label));

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        gap: 0.75,
      }}
    >
      <HtmlContent html={pack.label} sx={planTitleSx} />
      {hint && (
        <Typography
          component="span"
          sx={{
            fontSize: { xs: '0.75rem', md: '0.7rem' },
            fontWeight: 500,
            color: COLORS.muted,
            letterSpacing: '0.02em',
            lineHeight: 1.2,
          }}
        >
          ({hint})
        </Typography>
      )}
    </Box>
  );
};

const HighlightBadge = () => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: '50%',
      transform: 'translate(-50%, -50%)',
      bgcolor: COLORS.accent,
      color: '#fff',
      px: 2,
      py: 0.4,
      borderRadius: '999px',
      fontSize: '0.65rem',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      zIndex: 2,
    }}
  >
    Melhor custo benefício
  </Box>
);

const FeatureValue = ({ value }: { value: PlanFeatureValue }) => {
  if (typeof value === 'string') {
    return (
      <Typography
        component="span"
        sx={{ color: COLORS.foreground, fontWeight: 600, fontSize: 'inherit' }}
      >
        {value}
      </Typography>
    );
  }

  if (value) {
    return (
      <CheckIcon
        sx={{ width: 20, height: 20, color: COLORS.accent }}
        aria-label="Incluído"
      />
    );
  }

  return (
    <CloseIcon
      sx={{ width: 20, height: 20, color: COLORS.mutedDisabled }}
      aria-label="Não incluído"
    />
  );
};

const PlanPrice = ({
  original,
  discounted,
  savings,
  centered = false,
}: {
  original: string;
  discounted: string;
  savings: string;
  centered?: boolean;
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: centered ? 'center' : 'flex-start',
    }}
  >
    <Typography
      sx={{
        textDecoration: 'line-through',
        color: '#9e9e9e',
        fontSize: '0.875rem',
        mb: 0.5,
      }}
    >
      R$ {original}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
      <Typography
        sx={{ fontSize: '0.9rem', fontWeight: 500, color: COLORS.foreground }}
      >
        R$
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: '2.25rem', md: '2.5rem' },
          fontWeight: 700,
          color: COLORS.foreground,
          lineHeight: 1,
        }}
      >
        {discounted}
      </Typography>
      <Typography sx={{ fontSize: '0.85rem', color: COLORS.muted }}>
        /mês
      </Typography>
    </Box>
    <Box
      sx={{
        bgcolor: COLORS.savingsBg,
        borderRadius: '8px',
        px: 1.5,
        py: 0.5,
      }}
    >
      <Typography
        sx={{ fontSize: '0.75rem', color: COLORS.savingsInk, fontWeight: 500 }}
      >
        Economia de R$ {savings} em 12 meses
      </Typography>
    </Box>
  </Box>
);

const PlanCard = ({
  pack,
  highlight,
  onSelectPlan,
}: {
  pack: BeerPack;
  highlight: boolean;
  onSelectPlan: (pack: BeerPack) => void;
}) => {
  const quantity = getPackQuantity(pack.label);
  const features = getPlanFeatures(quantity);
  const savings = calcPlanSavings(
    pack.priceWithoutCup.original,
    pack.priceWithoutCup.discounted,
  );

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'visible',
        pt: highlight ? 2.5 : 0,
      }}
    >
      {highlight && <HighlightBadge />}
      <Card
        variant="outlined"
        sx={{
          borderRadius: '16px',
          border: highlight ? `2px solid ${COLORS.accent}` : `1.5px solid ${COLORS.border}`,
          bgcolor: highlight ? COLORS.accentLight : COLORS.surface,
          boxShadow: highlight ? '0 8px 24px rgba(201, 108, 30, 0.1)' : 'none',
          overflow: 'visible',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <PlanTitle pack={pack} />
          <HtmlContent
            html={pack.subtitle}
            sx={{
              fontSize: '0.75rem',
              color: COLORS.muted,
              mt: 0.5,
              '& b, & strong': { fontWeight: 700 },
            }}
          />

        <Box sx={{ mt: 2 }}>
          <PlanPrice
            original={pack.priceWithoutCup.original}
            discounted={pack.priceWithoutCup.discounted}
            savings={savings}
          />
        </Box>

        <Box component="ul" sx={{ mt: 3, mb: 0, p: 0, listStyle: 'none' }}>
          {features.map(feature => (
            <Box
              component="li"
              key={feature.label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                py: 1.25,
                borderTop: `1px solid ${COLORS.border}`,
                '&:first-of-type': { borderTop: 'none' },
              }}
            >
              <Typography sx={{ fontSize: '0.875rem', color: COLORS.muted }}>
                {feature.label}
              </Typography>
              <FeatureValue value={feature.included} />
            </Box>
          ))}
        </Box>

        <Button
          fullWidth
          variant={highlight ? 'contained' : 'outlined'}
          onClick={() => onSelectPlan(pack)}
          sx={{
            mt: 3,
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            py: 1.25,
            ...(highlight
              ? {
                  bgcolor: COLORS.accent,
                  color: '#fff',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: COLORS.accentHover,
                    boxShadow: 'none',
                  },
                }
              : {
                  borderColor: COLORS.accent,
                  color: COLORS.accent,
                  '&:hover': {
                    borderColor: COLORS.accentHover,
                    color: COLORS.accentHover,
                    bgcolor: 'transparent',
                  },
                }),
          }}
        >
          Escolher {stripHtml(pack.label)}
        </Button>
      </CardContent>
    </Card>
    </Box>
  );
};

const SubscriptionPlansSection = ({
  packs,
  onSelectPlan,
}: SubscriptionPlansSectionProps) => {
  const sortedPacks = [...packs].sort(
    (a, b) => getPackQuantity(a.label) - getPackQuantity(b.label),
  );
  const highlightPackId = getHighlightedPackId(sortedPacks);
  const featureLabels = getPlanFeatures(
    getPackQuantity(sortedPacks[0]?.label ?? ''),
  ).map(feature => feature.label);

  if (sortedPacks.length === 0) {
    return null;
  }

  return (
    <Box sx={{ maxWidth: 1152, mx: 'auto', overflow: 'visible' }}>
      <Box sx={{ textAlign: 'center', mb: 4, px: 1 }}>
        <Typography
          sx={{
            fontSize: '0.65rem',
            fontWeight: 700,
            letterSpacing: '0.2em',
            color: COLORS.accent,
            textTransform: 'uppercase',
            mb: 1,
          }}
        >
          Escolha sua dose mensal
        </Typography>
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            fontWeight: 700,
            color: COLORS.foreground,
            lineHeight: 1.2,
          }}
        >
          Três planos.{' '}
          <Box
            component="span"
            sx={{ color: COLORS.accent, fontStyle: 'italic' }}
          >
            Zero repetição.
          </Box>
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            fontSize: '0.875rem',
            color: COLORS.muted,
            maxWidth: 420,
            mx: 'auto',
          }}
        >
          Compare o que está incluído em cada plano e escolha o que combina com
          seu ritmo.
        </Typography>
      </Box>

      <Box
        sx={{
          display: { xs: 'grid', md: 'none' },
          gap: 2,
          px: 0.5,
          pt: 2.5,
          overflow: 'visible',
        }}
      >
        {sortedPacks.map(pack => (
          <PlanCard
            key={pack.id}
            pack={pack}
            highlight={pack.id === highlightPackId}
            onSelectPlan={onSelectPlan}
          />
        ))}
      </Box>

      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          pt: 2.5,
          overflow: 'visible',
        }}
      >
        <TableContainer
          sx={{
            bgcolor: COLORS.surface,
            borderRadius: '24px',
            border: `1px solid ${COLORS.border}`,
            overflow: 'visible',
            boxShadow: '0 24px 48px rgba(26, 16, 8, 0.08)',
          }}
        >
        <Table sx={{ width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  width: '25%',
                  p: 3,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: COLORS.muted,
                  borderBottom: `1px solid ${COLORS.border}`,
                }}
              >
                Benefícios
              </TableCell>
              {sortedPacks.map(pack => {
                const highlight = pack.id === highlightPackId;
                const savings = calcPlanSavings(
                  pack.priceWithoutCup.original,
                  pack.priceWithoutCup.discounted,
                );

                return (
                  <TableCell
                    key={pack.id}
                    sx={{
                      p: 3,
                      pt: highlight ? 4.5 : 3,
                      verticalAlign: 'top',
                      position: 'relative',
                      overflow: 'visible',
                      bgcolor: highlight ? COLORS.accentLight : COLORS.surface,
                      borderBottom: `1px solid ${COLORS.border}`,
                    }}
                  >
                    {highlight && <HighlightBadge />}
                    <PlanTitle pack={pack} />
                    <HtmlContent
                      html={pack.subtitle}
                      sx={{
                        fontSize: '0.75rem',
                        color: COLORS.muted,
                        mt: 0.5,
                        '& b, & strong': { fontWeight: 700 },
                      }}
                    />
                    <Box sx={{ mt: 2 }}>
                      <PlanPrice
                        original={pack.priceWithoutCup.original}
                        discounted={pack.priceWithoutCup.discounted}
                        savings={savings}
                      />
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {featureLabels.map((featureLabel, rowIdx) => (
              <TableRow key={featureLabel}>
                <TableCell
                  sx={{
                    p: 2,
                    px: 3,
                    fontSize: '0.875rem',
                    color: COLORS.muted,
                    borderTop: `1px solid ${COLORS.border}`,
                  }}
                >
                  {featureLabel}
                </TableCell>
                {sortedPacks.map(pack => {
                  const highlight = pack.id === highlightPackId;
                  const features = getPlanFeatures(getPackQuantity(pack.label));

                  return (
                    <TableCell
                      key={pack.id}
                      sx={{
                        p: 2,
                        px: 3,
                        fontSize: '0.875rem',
                        borderTop: `1px solid ${COLORS.border}`,
                        bgcolor: highlight ? COLORS.accentLighter : COLORS.surface,
                      }}
                    >
                      <FeatureValue value={features[rowIdx].included} />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            <TableRow>
              <TableCell
                sx={{ p: 3, borderTop: `1px solid ${COLORS.border}` }}
              />
              {sortedPacks.map(pack => {
                const highlight = pack.id === highlightPackId;

                return (
                  <TableCell
                    key={pack.id}
                    sx={{
                      p: 3,
                      borderTop: `1px solid ${COLORS.border}`,
                      bgcolor: highlight ? COLORS.accentLighter : COLORS.surface,
                    }}
                  >
                    <Button
                      fullWidth
                      variant={highlight ? 'contained' : 'outlined'}
                      onClick={() => onSelectPlan(pack)}
                      sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1.25,
                        ...(highlight
                          ? {
                              bgcolor: COLORS.accent,
                              color: '#fff',
                              boxShadow: 'none',
                              '&:hover': {
                                bgcolor: COLORS.accentHover,
                                boxShadow: 'none',
                              },
                            }
                          : {
                              borderColor: COLORS.accent,
                              color: COLORS.accent,
                              '&:hover': {
                                borderColor: COLORS.accentHover,
                                color: COLORS.accentHover,
                                bgcolor: 'transparent',
                              },
                            }),
                      }}
                    >
                      Escolher {stripHtml(pack.label)}
                    </Button>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      </Box>
    </Box>
  );
};

export default SubscriptionPlansSection;
